import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Globe, Search, BarChart3, TrendingUp, FileText, Calendar, Eye, RefreshCw } from 'lucide-react';
import { AppLayout } from '../layouts/AppLayout';
import { useAuth } from '../contexts/AuthContext';

export default function TransparencyPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [acts, setActs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Usar o tenant do usuário logado
  const tenantId = user?.tenant_id || 'SP';

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, actsRes, timelineRes] = await Promise.all([
        api.get(`/public/stats/${tenantId}`),
        api.get(`/public/acts/${tenantId}?limit=50`),
        api.get(`/public/timeline/${tenantId}?months=12`),
      ]);
      setStats(statsRes.data);
      setActs(actsRes.data.acts || []);
      setTimeline(timelineRes.data.timeline || []);
    } catch (error) {
      console.error('Erro ao carregar dados públicos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 3) return;
    try {
      const res = await api.get(`/public/search/${tenantId}?q=${searchQuery}`);
      setActs(res.data.results || []);
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const handleFilter = async () => {
    try {
      let url = `/public/acts/${tenantId}?limit=50`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (typeFilter && typeFilter !== 'all') url += `&type=${typeFilter}`;
      const res = await api.get(url);
      setActs(res.data.acts || []);
    } catch (error) {
      console.error('Erro ao filtrar:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'NOVO': 'bg-blue-500',
      'PLANEJADO': 'bg-yellow-500',
      'EM_EXECUCAO': 'bg-orange-500',
      'AGUARDANDO_EVIDENCIA': 'bg-blue-500',
      'EM_REVISAO': 'bg-indigo-500',
      'ENCERRADO': 'bg-green-500',
      'ARQUIVADO': 'bg-gray-500',
    };
    return <Badge className={colors[status] || 'bg-gray-500'}>{status?.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="space-y-6" data-testid="transparency-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <Globe className="h-8 w-8" />
          <h1 className="text-3xl font-bold">Portal de Conformidade</h1>
        </div>
        <p className="text-blue-100 text-lg">
          Acesso público aos atos de controle, evidências verificáveis e indicadores de conformidade institucional.
        </p>
        <p className="text-blue-200 text-sm mt-2">
          Última atualização: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString('pt-BR') : 'N/A'}
        </p>
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Taxa de Conformidade</p>
                <p className="text-3xl font-bold text-green-700">{stats?.compliance_rate || 0}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total de Atos</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_acts || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Atos em Atraso</p>
                <p className="text-2xl font-bold text-red-600">{stats?.delayed_acts || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taxa de Atraso</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.delay_rate || 0}%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribuições */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats?.status_distribution || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  {getStatusBadge(status)}
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats?.type_distribution || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{type}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Por Órgão de Controle</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats?.control_body_distribution || {}).map(([body, count]) => (
                <div key={body} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 truncate max-w-[200px]">{body}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Evolução Mensal</CardTitle>
            <CardDescription>Atos criados e concluídos por mês</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-40 overflow-x-auto pb-4">
              {timeline.map((month, i) => (
                <div key={i} className="flex flex-col items-center min-w-[60px]">
                  <div className="flex gap-1 items-end h-24">
                    <div
                      className="w-4 bg-blue-500 rounded-t"
                      style={{ height: `${Math.max(10, (month.created / Math.max(...timeline.map(t => t.created))) * 100)}%` }}
                      title={`Criados: ${month.created}`}
                    />
                    <div
                      className="w-4 bg-green-500 rounded-t"
                      style={{ height: `${Math.max(10, (month.completed / Math.max(...timeline.map(t => t.created || 1))) * 100)}%` }}
                      title={`Concluídos: ${month.completed}`}
                    />
                  </div>
                  <span className="text-xs text-slate-500 mt-2">{month._id}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 justify-center mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded" />
                <span className="text-sm text-slate-600">Criados</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded" />
                <span className="text-sm text-slate-600">Concluídos</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Busca e Lista de Atos */}
      <Card>
        <CardHeader>
          <CardTitle>Consulta de Atos de Controle</CardTitle>
          <CardDescription>Dados públicos anonimizados</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por número, área temática..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="NOVO">Novo</SelectItem>
                <SelectItem value="EM_EXECUCAO">Em Execução</SelectItem>
                <SelectItem value="ENCERRADO">Encerrado</SelectItem>
                <SelectItem value="ARQUIVADO">Arquivado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="AUDITORIA">Auditoria</SelectItem>
                <SelectItem value="INSPECAO">Inspeção</SelectItem>
                <SelectItem value="RECOMENDACAO">Recomendação</SelectItem>
                <SelectItem value="DETERMINACAO">Determinação</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleFilter} data-testid="btn-filter">
              Filtrar
            </Button>
          </div>

          {/* Tabela */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Órgão</TableHead>
                <TableHead>Área</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {acts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    Nenhum ato encontrado
                  </TableCell>
                </TableRow>
              ) : (
                acts.map((act) => (
                  <TableRow key={act.act_id}>
                    <TableCell className="font-mono text-sm">{act.act_number || act.act_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{act.type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">{act.control_body}</TableCell>
                    <TableCell className="text-sm">{act.thematic_area}</TableCell>
                    <TableCell>{getStatusBadge(act.status)}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {act.created_at ? new Date(act.created_at).toLocaleDateString('pt-BR') : '-'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-slate-500 py-4">
        <p>Portal de Conformidade — BlueGov</p>
        <p>Dados atualizados automaticamente. Informações anonimizadas conforme LGPD.</p>
      </div>
    </div>
    </AppLayout>
  );
}
